import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { ROW } from "@/components/FinderRows";

const CLE = "rb_fichiers_colonnes";
const CLE_TRI = "rb_fichiers_tri";
const DEFAUT = { nom: 460, date: 170, contenu: 130, taille: 90 };
const MIN = { nom: 180, date: 90, contenu: 80, taille: 60 };
const COLONNES = [["nom", "Nom", ""], ["date", "Date", ""], ["contenu", "Contenu", "text-right"], ["taille", "Taille", "text-right"]];
const VAL = { nom: (f) => f.nom, date: (f) => f.importe_le || "", contenu: (f) => f.lignes || 0, taille: (f) => f.taille || 0 };

const lireJson = (cle, defaut) => {
  try { return JSON.parse(localStorage.getItem(cle)) ?? defaut; } catch { return defaut; }
};

export const useLargeurs = () => {
  const [l, setL] = useState(() => ({ ...DEFAUT, ...lireJson(CLE, {}) }));
  const [tri, setTri] = useState(() => lireJson(CLE_TRI, null));
  useEffect(() => { localStorage.setItem(CLE, JSON.stringify(l)); }, [l]);
  useEffect(() => { if (tri) localStorage.setItem(CLE_TRI, JSON.stringify(tri)); else localStorage.removeItem(CLE_TRI); }, [tri]);
  const changer = useCallback((col, px) => setL((x) => ({ ...x, [col]: Math.max(MIN[col], Math.round(px)) })), []);
  const trier = useCallback((col) => setTri((t) => {
    if (t?.col !== col) return { col, dir: "asc" };
    return t.dir === "asc" ? { col, dir: "desc" } : null;
  }), []);
  const reinit = useCallback(() => { setL(DEFAUT); setTri(null); }, []);
  const perso = !!tri || COLONNES.some(([c]) => l[c] !== DEFAUT[c]);
  const gabarit = `${l.nom}px ${l.date}px ${l.contenu}px ${l.taille}px 76px`;
  return { largeurs: l, changer, gabarit, tri, trier, reinit, perso };
};

export const trierFichiers = (fs, tri) => {
  if (!tri) return fs;
  const v = VAL[tri.col];
  const s = tri.dir === "asc" ? 1 : -1;
  return [...fs].sort((a, b) => {
    const x = v(a);
    const y = v(b);
    return s * (typeof x === "number" ? x - y : String(x).localeCompare(String(y), "fr", { numeric: true, sensitivity: "base" }));
  });
};

const largeurTexte = (noms) => {
  const ctx = document.createElement("canvas").getContext("2d");
  ctx.font = "14px system-ui, sans-serif";
  return Math.max(0, ...noms.map((n) => ctx.measureText(n).width));
};

const largeurContenu = (e) => {
  const r = document.createRange();
  r.selectNodeContents(e);
  return r.getBoundingClientRect().width;
};
const largeurDom = (col) => Math.ceil(Math.max(0, ...[...document.querySelectorAll(`[data-finder-col="${col}"]`)].map(largeurContenu)));

const Poignee = ({ col, largeur, changer, autoFit }) => {
  const debut = (e) => {
    e.preventDefault();
    const x0 = e.clientX;
    const bouger = (ev) => changer(col, largeur + ev.clientX - x0);
    const fin = () => {
      window.removeEventListener("mousemove", bouger);
      window.removeEventListener("mouseup", fin);
      document.body.style.cursor = "";
    };
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", bouger);
    window.addEventListener("mouseup", fin);
  };
  return (
    <span onMouseDown={debut} onDoubleClick={autoFit} title="Glisser pour élargir · double-clic pour ajuster"
      className="group/p absolute -right-2 top-0 z-10 hidden h-full w-4 cursor-col-resize justify-center sm:flex" data-testid={`colonne-poignee-${col}`}>
      <span className="h-full w-px bg-slate-200 transition-colors group-hover/p:w-0.5 group-hover/p:bg-sky-500" />
    </span>
  );
};

const Fleche = ({ dir }) => (dir === "asc" ? <ArrowUp className="h-3 w-3 text-sky-600" /> : <ArrowDown className="h-3 w-3 text-sky-600" />);

export const FinderEntetes = ({ largeurs, changer, noms, tri, trier }) => (
  <div className={`${ROW} border-b border-slate-100 py-2 text-xs font-semibold text-slate-500`} data-testid="finder-entetes">
    {COLONNES.map(([col, label, align]) => (
      <span key={col} className={`relative ${col === "nom" ? "pl-1" : "hidden sm:block"} ${align}`} data-testid={`colonne-${col}`}>
        <button type="button" onClick={() => trier(col)} data-finder-col={col === "nom" ? undefined : col} data-testid={`colonne-tri-${col}`}
          className="inline-flex items-center gap-1 whitespace-nowrap transition-colors hover:text-slate-800">
          {label}{tri?.col === col && <Fleche dir={tri.dir} />}
        </button>
        <Poignee col={col} largeur={largeurs[col]} changer={changer}
          autoFit={() => changer(col, col === "nom" ? largeurTexte(noms) + 210 : largeurDom(col) + 8)} />
      </span>
    ))}
    <span />
  </div>
);
